import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useBackHomePath } from "@/lib/useBackHomePath";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, BookOpen, FileText, Link as LinkIcon, Trash2, Pencil, Paperclip, X, Upload, Image as ImageIcon, FileType } from "lucide-react";

const ConfirmButton = ({ onConfirm, testId }) => {
    const [armed, setArmed] = useState(false);
    return (
        <button type="button" onClick={() => { if (armed) { onConfirm(); setArmed(false); } else { setArmed(true); setTimeout(() => setArmed(false), 3000); } }} data-testid={testId}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition ${armed ? "bg-terracotta text-white" : "bg-terracotta/10 text-terracotta hover:bg-terracotta/20"}`}>
            <Trash2 className="w-3 h-3" /> {armed ? "Konfirmo" : "Fshi"}
        </button>
    );
};

const fileIconFor = (ct = "", name = "") => {
    if (ct.startsWith("image/")) return <ImageIcon className="w-4 h-4" />;
    if (ct === "application/pdf" || name.toLowerCase().endsWith(".pdf")) return <FileText className="w-4 h-4" />;
    return <FileType className="w-4 h-4" />;
};

const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const ALLOWED = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.mp4,.mp3,.zip";

export default function TeacherLMS() {
    const [posts, setPosts] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState({ subject_id: "", class_id: "", title: "", body: "", url: "", kind: "text", section: "", attachments: [] });
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const backPath = useBackHomePath();

    const refresh = async () => {
        setLoading(true);
        try {
            const [p, s, c] = await Promise.all([api.get("/lms/posts"), api.get("/teacher/my-subjects"), api.get("/classes")]);
            setPosts(p.data); setSubjects(s.data); setClasses(c.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const kind = form.attachments.length > 0 ? "file" : (form.url ? "link" : "text");
            const payload = { ...form, class_id: form.class_id || null, kind };
            if (edit) {
                await api.patch(`/lms/post/${edit.id}`, payload);
            } else {
                await api.post("/lms/post", payload);
            }
            setForm({ subject_id: "", class_id: "", title: "", body: "", url: "", kind: "text", section: "", attachments: [] });
            setOpen(false); setEdit(null);
            refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    const del = async (pid) => { await api.delete(`/lms/post/${pid}`); refresh(); };

    const startEdit = (p) => {
        setEdit(p);
        setForm({
            subject_id: p.subject_id, class_id: p.class_id || "", title: p.title,
            body: p.body || "", url: p.url || "", kind: p.kind || "text",
            section: p.section || "",
            attachments: p.attachments || [],
        });
        setOpen(true);
    };

    const onFilesPicked = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setUploading(true); setErr("");
        try {
            const uploaded = [];
            for (const f of files) {
                const fd = new FormData(); fd.append("file", f);
                const r = await api.post("/lms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(r.data);
            }
            setForm((s) => ({ ...s, attachments: [...s.attachments, ...uploaded] }));
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeAttachment = (file_id) => setForm((s) => ({ ...s, attachments: s.attachments.filter((a) => a.file_id !== file_id) }));

    return (
        <Layout>
            <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">LMS</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Materialet mësimore</h1>
                    <p className="text-slate-600 mt-2 text-sm">Publiko materiale për lëndë specifike — PDF, PowerPoint, imazhe ose linke</p>
                </div>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEdit(null); setForm({ subject_id: "", class_id: "", title: "", body: "", url: "", kind: "text", section: "", attachments: [] }); } }}>
                    <DialogTrigger asChild>
                        <Button data-testid="lms-new-post-btn" className="bg-oxford hover:bg-oxfordhover text-white gap-2">
                            <Plus className="w-4 h-4" /> Material i ri
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="font-heading">{edit ? "Edito materialin" : "Material i ri"}</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} required className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="lms-form-subject">
                                <option value="">— Zgjidh lëndën —</option>
                                {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                            <select value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="lms-form-class">
                                <option value="">— Të gjitha klasat —</option>
                                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <Input placeholder="Titulli" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="lms-form-title" />
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Seksioni (opsional)</label>
                                <Input placeholder="p.sh. Javë 1 • Testet • Material shtesë" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} data-testid="lms-form-section" list="lms-section-suggestions" />
                                <datalist id="lms-section-suggestions">
                                    <option value="Javë 1" />
                                    <option value="Javë 2" />
                                    <option value="Javë 3" />
                                    <option value="Leksione" />
                                    <option value="Ushtrime" />
                                    <option value="Testet" />
                                    <option value="Material shtesë" />
                                    <option value="Projekte" />
                                </datalist>
                            </div>
                            <Textarea placeholder="Përshkrimi / mësimi" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={3} data-testid="lms-form-body" />
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Link (opsional)</label>
                                <Input placeholder="https://drive.google.com/..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="lms-form-url" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Bashkëngjitje (PDF, PPT, Imazhe — max 50MB)</label>
                                <label className={`flex items-center justify-center gap-2 h-12 rounded-md border-2 border-dashed cursor-pointer transition ${uploading ? "border-oxford bg-oxford/5" : "border-slate-300 hover:border-oxford hover:bg-bone/30"}`} data-testid="lms-form-file-picker">
                                    <Upload className="w-4 h-4" />
                                    <span className="text-sm font-semibold">{uploading ? "Duke ngarkuar…" : "Zgjidh files"}</span>
                                    <input type="file" multiple accept={ALLOWED} onChange={onFilesPicked} disabled={uploading} className="hidden" data-testid="lms-form-file-input" />
                                </label>
                                {form.attachments.length > 0 && (
                                    <div className="mt-2 space-y-1.5">
                                        {form.attachments.map((a) => (
                                            <div key={a.file_id} className="flex items-center gap-2 px-3 py-2 rounded-md bg-bone/40 border border-slate-200 text-sm" data-testid={`lms-form-att-${a.file_id}`}>
                                                <span className="text-slate-500">{fileIconFor(a.content_type, a.filename)}</span>
                                                <span className="flex-1 truncate font-medium text-slate-800">{a.filename}</span>
                                                <span className="text-[11px] text-slate-500">{fmtSize(a.size)}</span>
                                                <button type="button" onClick={() => removeAttachment(a.file_id)} className="text-terracotta hover:bg-terracotta/10 rounded p-1" data-testid={`lms-form-att-remove-${a.file_id}`}><X className="w-3.5 h-3.5" /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {err && <div className="text-xs text-terracotta">{err}</div>}
                            <DialogFooter>
                                <Button type="submit" disabled={busy || uploading} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="lms-form-submit">{busy ? "Duke ruajtur…" : "Publiko"}</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {loading ? <div>Duke ngarkuar…</div> : posts.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center"><BookOpen className="w-10 h-10 text-slate-400 mx-auto mb-4" /><div className="font-heading text-xl font-bold">Pa materiale ende</div></div>
            ) : (
                <div className="space-y-3">
                    {posts.map((p) => {
                        const Icon = (p.attachments?.length > 0) ? Paperclip : (p.kind === "link" ? LinkIcon : BookOpen);
                        return (
                            <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex gap-4" data-testid={`teacher-lms-post-${p.id}`}>
                                <div className="w-12 h-12 rounded-xl bg-sage/10 text-sage flex items-center justify-center shrink-0"><Icon className="w-6 h-6" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className="font-heading font-bold text-slate-900">{p.title}</span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-oxford/10 text-oxford text-[11px] font-bold">{p.subject_name}</span>
                                        {p.section && <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-sage/15 text-sage text-[11px] font-bold border border-sage/30">{p.section}</span>}
                                        {p.class_id && <span className="text-[10px] text-slate-500">• Klasë specifike</span>}
                                    </div>
                                    <div className="text-xs text-slate-500 mb-2">{p.teacher_name} • {p.created_at?.slice(0, 10)}</div>
                                    {p.body && <p className="text-slate-700 text-sm leading-relaxed mb-2">{p.body}</p>}
                                    {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-oxford font-semibold text-sm hover:underline inline-flex items-center gap-1.5"><LinkIcon className="w-4 h-4" /> Hap link</a>}
                                    {p.attachments?.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {p.attachments.map((a) => (
                                                <a key={a.file_id} href={`${api.defaults.baseURL}/lms/file/${a.file_id}?auth=${localStorage.getItem("token")}`} target="_blank" rel="noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-sage/10 text-sage hover:bg-sage/20 text-xs font-semibold border border-sage/30 transition"
                                                    data-testid={`teacher-lms-att-${a.file_id}`}>
                                                    {fileIconFor(a.content_type, a.filename)} {a.filename} <span className="text-[10px] text-slate-500">({fmtSize(a.size)})</span>
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1 items-end">
                                    <button onClick={() => startEdit(p)} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`teacher-lms-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                                    <ConfirmButton onConfirm={() => del(p.id)} testId={`teacher-lms-delete-${p.id}`} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Layout>
    );
}
